import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetCountriesQuery, useLazyGetZipCodesQuery } from "store/api/dictionaryApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { UITypes } from "types"

import { convertDataToOptions } from "utils/convertData"
import { useUserProfile } from "hooks/useUserProfile"
import { SelectDynamic } from "components/form/SelectDynamic"

import { useAccountSettingsTabGuard } from "./navigationGuard"
import { AccountPageShell, Field, Footer, getEnterpriseProfileInitialState, isEnterpriseProfileDirty } from "./shared"
import { useUpdateEnterpriseAccount } from "./useUpdateEnterpriseAccount"

import styles from "./index.module.scss"

export const AccountProfilePage = () => {
  const dispatch = useAppDispatch()
  const { enterpriseCountry } = useUserProfile()
  const currentZip = useAppSelector(state => state.tempData.currentZip)
  const { enterprise, isLoading, updateEnterprise } = useUpdateEnterpriseAccount()
  const initialValues = useMemo(
    () => getEnterpriseProfileInitialState(enterprise, enterpriseCountry),
    [enterprise, enterpriseCountry],
  )
  const [profileValues, setProfileValues] = useState(initialValues)
  const isDirty = Boolean(enterprise && isEnterpriseProfileDirty(enterprise, enterpriseCountry, profileValues))

  const handleProfileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setProfileValues(prev => ({
      ...prev,
      [name]: value,
      ...(name === "city" ? { cityId: undefined } : {}),
      ...(name === "state" ? { stateId: undefined } : {}),
    }))
  }

  const handleCountryChange = (country: UITypes.Option[]) => {
    if (profileValues.country[0]?.value === country[0]?.value) {
      return
    }

    dispatch(setCurrentZip(undefined))
    setProfileValues(prev => ({
      ...prev,
      country,
      zip: [],
      state: "",
      stateId: undefined,
      city: "",
      cityId: undefined,
    }))
  }

  const handleZipChange = (zip: UITypes.Option[]) => {
    if (!zip.length) {
      dispatch(setCurrentZip(undefined))
      setProfileValues(prev => ({ ...prev, zip, state: "", stateId: undefined, city: "", cityId: undefined }))
      return
    }

    setProfileValues(prev => ({ ...prev, zip }))
  }

  useEffect(() => {
    setProfileValues(initialValues)
  }, [initialValues])

  const handleSave = useCallback(async () => {
    if (!profileValues.companyName.trim()) {
      toast.error("Company name is required")
      return false
    }

    return updateEnterprise({ profileValues })
  }, [profileValues, updateEnterprise])

  useAccountSettingsTabGuard({
    isDirty,
    isSaving: isLoading,
    onSave: handleSave,
  })

  useEffect(() => {
    if (!currentZip?.city) return

    setProfileValues(prev => ({
      ...prev,
      country: convertDataToOptions({ data: [currentZip.country] }, "id", "name"),
      zip: convertDataToOptions({ data: [currentZip] }, "id", "code"),
      state: currentZip.state?.name || "",
      stateId: currentZip.state?.id,
      city: currentZip.city?.name || "",
      cityId: currentZip.city?.id,
    }))
  }, [currentZip])

  return (
    <AccountPageShell footer={<Footer isLoading={isLoading} isSaveDisabled={!isDirty} onSave={handleSave} />}>
      <h1 className={styles.title}>Profile</h1>
      <div className={styles.formGrid}>
        <Field
          className={styles.fieldNarrow}
          label="Company name *"
          name="companyName"
          value={profileValues.companyName}
          onChange={handleProfileChange}
        />
        <span />
        <Field label="Address 1" name="address1" value={profileValues.address1} onChange={handleProfileChange} />
        <Field
          label="Address 2"
          name="address2"
          value={profileValues.address2}
          placeholder="Enter address"
          onChange={handleProfileChange}
        />
        <SelectDynamic
          label="Country"
          searchFieldName="country"
          valueName="id"
          labelName="name"
          name="country"
          values={profileValues.country}
          className={styles.selectField}
          useLazyQuery={useLazyGetCountriesQuery}
          placeholder="Enter Country"
          onChange={handleCountryChange}
        />
        <SelectDynamic
          label="Zip code"
          searchFieldName="code"
          valueName="id"
          labelName="code"
          name="zip"
          values={profileValues.zip}
          className={styles.selectField}
          useLazyQuery={useLazyGetZipCodesQuery}
          placeholder="Enter Zip code"
          onChange={handleZipChange}
          queryParams={{ country_id: (profileValues.country[0]?.value || 1) as unknown as number }}
          isSearchable
          backspaceDelete
        />
        <Field label="State" name="state" value={profileValues.state} onChange={handleProfileChange} />
        <Field label="City" name="city" value={profileValues.city} onChange={handleProfileChange} />
      </div>
    </AccountPageShell>
  )
}
